import { Component, HostBinding, Input } from '@angular/core';
import { Border, borderToStyle } from 'shared/design-system/data-access/border';
import {
    BorderRadiusSize,
    MinSize,
    PaddingSize,
    PercentSize,
    PxSize,
    RemSize,
    Size,
    VarSize,
    VhSize,
} from 'shared/design-system/data-access/layout';
import { BreakpointEnum } from 'shared/design-system/data-access/responsive';
import { ColorEnum } from 'shared/design-system/data-access/theme';

@Component({
    selector: 'it-column',
    imports: [],
    templateUrl: './column.component.html',
    styleUrls: ['./column.component.scss'],
})
export class ColumnComponent {
    @Input() axisY?:
        | 'flex-start'
        | 'center'
        | 'flex-end'
        | 'space-evenly'
        | 'space-between';
    @Input() axisX?: 'flex-start' | 'center' | 'flex-end';
    @Input() flex?: Size;
    @Input() gap?: RemSize = '2rem';
    @Input() padding?: PaddingSize | PxSize = '2rem';
    @Input() backgroundColor?: ColorEnum;
    @Input() color?: ColorEnum;
    @Input() border?: Border;
    @Input() borderRadius?: BorderRadiusSize;
    @Input() margin?: PaddingSize;
    @Input() scroll?: boolean;
    @Input() minWidth?: PxSize | VarSize | RemSize;
    @Input() width?: PxSize | VarSize | PercentSize;
    @Input() clampWidth?: PxSize | BreakpointEnum | VarSize | MinSize | RemSize;
    @Input() minHeight?: PxSize | VarSize;
    @Input() height?: PxSize | VarSize | PercentSize | RemSize;
    @Input() clampHeight?: PxSize | VhSize | PercentSize | RemSize;
    @Input() alignSelf?: 'center' | 'flex-start' | 'flex-end' | 'stretch';

    @HostBinding('style.justify-content')
    get justifyContent() {
        return this.axisY;
    }

    @HostBinding('style.align-items')
    get alignItems() {
        return this.axisX;
    }

    @HostBinding('style.align-self')
    get alignSelfStyle() {
        return this.alignSelf;
    }

    @HostBinding('style.flex')
    get flexStyle() {
        return this.flex;
    }

    @HostBinding('style.gap')
    get gapStyle() {
        return this.gap;
    }

    @HostBinding('style.padding')
    get paddingStyle() {
        return this.padding;
    }

    @HostBinding('style.background')
    get backgroundStyle() {
        return this.backgroundColor;
    }

    @HostBinding('style.color')
    get colorStyle() {
        return this.color;
    }

    @HostBinding('style.overflow-y')
    get overflowYStyle() {
        return this.scroll ? 'auto' : undefined;
    }

    @HostBinding('style.overflow')
    get overflowStyle() {
        return this.borderRadius ? 'hidden' : undefined;
    }

    @HostBinding('style.border-top')
    get borderTop() {
        return borderToStyle(this.border)?.borderTop;
    }

    @HostBinding('style.border-right')
    get borderRight() {
        return borderToStyle(this.border)?.borderRight;
    }

    @HostBinding('style.border-bottom')
    get borderBottom() {
        return borderToStyle(this.border)?.borderBottom;
    }

    @HostBinding('style.border-left')
    get borderLeft() {
        return borderToStyle(this.border)?.borderLeft;
    }

    @HostBinding('style.border-radius')
    get borderRadiusStyle() {
        return this.borderRadius;
    }

    @HostBinding('style.width')
    get widthStyle() {
        return this.clampWidth ? `min(${this.clampWidth},100%)` : this.width;
    }

    @HostBinding('style.min-width')
    get minWidthStyle() {
        return this.minWidth;
    }

    @HostBinding('style.max-width')
    get maxWidthStyle() {
        return this.clampWidth;
    }

    @HostBinding('style.min-height')
    get minHeightStyle() {
        return this.minHeight;
    }

    @HostBinding('style.height')
    get heightStyle() {
        return this.clampHeight ? `min(${this.clampHeight},100%)` : this.height;
    }

    @HostBinding('style.max-height')
    get maxHeightStyle() {
        return this.clampHeight;
    }

    @HostBinding('style.margin')
    get marginStyle() {
        return this.margin;
    }
}
